import { useRef, useEffect } from "react";
import * as THREE from "three";

// Raw Three.js canvas - no R3F reconciler issues
const useThreeScene = (canvasRef, setupFn) => {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(canvas.clientWidth, canvas.clientHeight);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, canvas.clientWidth / canvas.clientHeight, 0.1, 100);

    const cleanup = setupFn(scene, camera, renderer);

    let animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      if (cleanup?.update) cleanup.update();
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", onResize);
      renderer.dispose();
      if (cleanup?.dispose) cleanup.dispose();
    };
  }, [canvasRef, setupFn]);
};

// ====== SKY SCENE (Balon Teman) ======
export const SkyScene3D = () => {
  const ref = useRef(null);

  useThreeScene(ref, (scene, camera) => {
    camera.position.set(0, 0, 5);

    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const sun = new THREE.DirectionalLight(0xfffae6, 0.8);
    sun.position.set(5, 5, 5);
    scene.add(sun);

    const objects = [];

    // Clouds - white puffy spheres
    const cloudMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.9, transparent: true, opacity: 0.6 });
    const cloudGeo = new THREE.SphereGeometry(0.3, 8, 8);
    const cloudPositions = [[-3, 2, -3], [2, 1.5, -4], [0, 2.5, -5], [-1, 1.8, -3.5]];
    cloudPositions.forEach(pos => {
      const g = new THREE.Group();
      for (let i = -1; i <= 1; i++) {
        const m = new THREE.Mesh(cloudGeo, cloudMat);
        m.position.set(i * 0.25, i === 0 ? 0.1 : 0, 0);
        m.scale.setScalar(0.8 + (i === 0 ? 0.3 : 0));
        g.add(m);
      }
      g.position.set(...pos);
      scene.add(g);
      objects.push({ obj: g, type: "cloud", speed: (Math.random() - 0.5) * 0.003, baseX: pos[0] });
    });

    // Stars - octahedron
    const starMat = new THREE.MeshStandardMaterial({ color: 0xffd700, roughness: 0.2, metalness: 0.5, emissive: 0xffd700, emissiveIntensity: 0.4 });
    const starGeo = new THREE.OctahedronGeometry(0.15, 0);
    [[-2, 1, -2], [2.5, 2, -3], [0, 3, -2.5]].forEach(pos => {
      const m = new THREE.Mesh(starGeo, starMat);
      m.position.set(...pos);
      scene.add(m);
      objects.push({ obj: m, type: "star", offset: Math.random() * 6 });
    });

    // Colorful spheres
    const sphereGeo = new THREE.SphereGeometry(0.2, 12, 12);
    [
      { pos: [-3, -1, -2], color: 0xff69b4 },
      { pos: [3, 0, -3], color: 0x87ceeb },
      { pos: [1, -1.5, -1.5], color: 0x98fb98 },
    ].forEach(({ pos, color }) => {
      const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.4 });
      const m = new THREE.Mesh(sphereGeo, mat);
      m.position.set(...pos);
      scene.add(m);
      objects.push({ obj: m, type: "float", offset: Math.random() * 6, baseY: pos[1] });
    });

    const clock = new THREE.Clock();
    return {
      update: () => {
        const t = clock.getElapsedTime();
        objects.forEach(o => {
          if (o.type === "cloud") {
            o.obj.position.x += o.speed;
            if (o.obj.position.x > 5) o.obj.position.x = -5;
            if (o.obj.position.x < -5) o.obj.position.x = 5;
          }
          if (o.type === "star") {
            o.obj.rotation.y += 0.02;
            o.obj.position.y += Math.sin(t + o.offset) * 0.002;
            const s = 1 + Math.sin(t * 2 + o.offset) * 0.15;
            o.obj.scale.setScalar(s);
          }
          if (o.type === "float") {
            o.obj.position.y = o.baseY + Math.sin(t + o.offset) * 0.3;
            o.obj.rotation.x += 0.005;
          }
        });
      },
    };
  });

  return <canvas ref={ref} style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', width: '100%', height: '100%' }} />;
};

// ====== FIELD SCENE (Dorong Bola) ======
export const FieldScene3D = () => {
  const ref = useRef(null);

  useThreeScene(ref, (scene, camera) => {
    camera.position.set(0, 1, 5);

    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const sun = new THREE.DirectionalLight(0xfffae6, 0.9);
    sun.position.set(5, 8, 3);
    scene.add(sun);

    const objects = [];

    // Ground
    const groundGeo = new THREE.PlaneGeometry(20, 20);
    const groundMat = new THREE.MeshStandardMaterial({ color: 0x7ec850, roughness: 0.8 });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -2;
    scene.add(ground);

    // Trees
    const trunkGeo = new THREE.CylinderGeometry(0.08, 0.12, 0.8, 6);
    const trunkMat = new THREE.MeshStandardMaterial({ color: 0x8b4513, roughness: 0.9 });
    const leafGeo1 = new THREE.ConeGeometry(0.4, 0.8, 8);
    const leafGeo2 = new THREE.ConeGeometry(0.3, 0.6, 8);
    const leafMat1 = new THREE.MeshStandardMaterial({ color: 0x228b22, roughness: 0.7 });
    const leafMat2 = new THREE.MeshStandardMaterial({ color: 0x32cd32, roughness: 0.7 });

    [[-3, -2, -3], [4, -2, -4], [-1, -2, -5], [2.5, -2, -6]].forEach(pos => {
      const g = new THREE.Group();
      const trunk = new THREE.Mesh(trunkGeo, trunkMat);
      trunk.position.y = 0.4;
      g.add(trunk);
      const leaf1 = new THREE.Mesh(leafGeo1, leafMat1);
      leaf1.position.y = 1;
      g.add(leaf1);
      const leaf2 = new THREE.Mesh(leafGeo2, leafMat2);
      leaf2.position.y = 1.3;
      g.add(leaf2);
      g.position.set(...pos);
      scene.add(g);
      objects.push({ obj: g, type: "tree", baseX: pos[0] });
    });

    // Flowers
    const flowerGeo = new THREE.CircleGeometry(0.08, 6);
    [
      { pos: [-2, -1.98, -1], color: 0xff69b4 },
      { pos: [1, -1.98, -1.5], color: 0xffd700 },
      { pos: [3, -1.98, -2], color: 0xff6347 },
    ].forEach(({ pos, color }) => {
      const mat = new THREE.MeshStandardMaterial({ color });
      const m = new THREE.Mesh(flowerGeo, mat);
      m.position.set(...pos);
      m.rotation.x = -Math.PI / 2;
      scene.add(m);
      objects.push({ obj: m, type: "flower", offset: Math.random() * 6 });
    });

    // Clouds
    const cMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.9, transparent: true, opacity: 0.6 });
    const cGeo = new THREE.SphereGeometry(0.25, 8, 8);
    [[-2, 2, -4], [3, 2.5, -5]].forEach(pos => {
      const g = new THREE.Group();
      [-0.2, 0, 0.2].forEach((ox, i) => {
        const m = new THREE.Mesh(cGeo, cMat);
        m.position.set(ox, i === 1 ? 0.08 : 0, 0);
        g.add(m);
      });
      g.position.set(...pos);
      scene.add(g);
      objects.push({ obj: g, type: "cloud", speed: (Math.random() - 0.5) * 0.002 });
    });

    // Sun sphere
    const sunSphere = new THREE.Mesh(
      new THREE.SphereGeometry(0.5, 12, 12),
      new THREE.MeshStandardMaterial({ color: 0xffd700, emissive: 0xffa500, emissiveIntensity: 0.5 })
    );
    sunSphere.position.set(4, 3, -8);
    scene.add(sunSphere);
    objects.push({ obj: sunSphere, type: "float", offset: 0, baseY: 3 });

    const clock = new THREE.Clock();
    return {
      update: () => {
        const t = clock.getElapsedTime();
        objects.forEach(o => {
          if (o.type === "tree") o.obj.rotation.z = Math.sin(t * 0.5 + o.baseX) * 0.02;
          if (o.type === "flower") o.obj.rotation.z = Math.sin(t + o.offset) * 0.3;
          if (o.type === "cloud") {
            o.obj.position.x += o.speed;
            if (o.obj.position.x > 6) o.obj.position.x = -6;
            if (o.obj.position.x < -6) o.obj.position.x = 6;
          }
          if (o.type === "float") {
            o.obj.position.y = o.baseY + Math.sin(t * 0.3 + o.offset) * 0.1;
          }
        });
      },
    };
  });

  return <canvas ref={ref} style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', width: '100%', height: '100%' }} />;
};

// ====== KITCHEN SCENE (Masak Bareng) ======
export const KitchenScene3D = () => {
  const ref = useRef(null);

  useThreeScene(ref, (scene, camera) => {
    camera.position.set(0, 0.5, 4);

    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const point = new THREE.PointLight(0xfff5e1, 0.8);
    point.position.set(0, 3, 2);
    scene.add(point);
    const dir = new THREE.DirectionalLight(0xffffff, 0.6);
    dir.position.set(-3, 4, 3);
    scene.add(dir);

    const objects = [];

    // Back wall
    const wall = new THREE.Mesh(
      new THREE.PlaneGeometry(8, 5),
      new THREE.MeshStandardMaterial({ color: 0xfff8e1, roughness: 0.9 })
    );
    wall.position.set(0, 0, -2);
    scene.add(wall);

    // Counter
    const counter = new THREE.Mesh(
      new THREE.BoxGeometry(6, 0.15, 2),
      new THREE.MeshStandardMaterial({ color: 0x8d6e63, roughness: 0.7 })
    );
    counter.position.set(0, -1.5, -1);
    scene.add(counter);

    // Pots
    const potGeo = new THREE.CylinderGeometry(0.25, 0.3, 0.3, 12);
    const potMat = new THREE.MeshStandardMaterial({ color: 0xc0c0c0, roughness: 0.3, metalness: 0.7 });
    [[-1, -1.2, -0.5], [1.2, -1.2, -0.8]].forEach((pos, i) => {
      const m = new THREE.Mesh(potGeo, potMat);
      m.position.set(...pos);
      scene.add(m);
      objects.push({ obj: m, type: "pot", offset: i * 2, baseY: pos[1] });
    });

    // Steam puffs
    const steamGeo = new THREE.SphereGeometry(0.06, 6, 6);
    const steamMat = new THREE.MeshStandardMaterial({ color: 0xffffff, transparent: true, opacity: 0.3 });
    [[-1, -0.8, -0.5], [-0.9, -0.7, -0.5], [1.2, -0.8, -0.8], [1.3, -0.7, -0.8]].forEach((pos, i) => {
      const m = new THREE.Mesh(steamGeo, steamMat.clone());
      m.position.set(...pos);
      scene.add(m);
      objects.push({ obj: m, type: "steam", offset: i * 1.5, baseY: pos[1] });
    });

    // Floating decorations
    const starGeo = new THREE.OctahedronGeometry(0.12, 0);
    const starMat = new THREE.MeshStandardMaterial({ color: 0xffd700, roughness: 0.2, metalness: 0.5, emissive: 0xffd700, emissiveIntensity: 0.3 });
    const star = new THREE.Mesh(starGeo, starMat);
    star.position.set(2.5, 1, -1);
    scene.add(star);
    objects.push({ obj: star, type: "star", offset: 0 });

    // Spice jars
    const jarGeo = new THREE.CylinderGeometry(0.06, 0.06, 0.2, 8);
    [0xff6347, 0xffd700, 0x228b22, 0x8b4513].forEach((color, i) => {
      const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.4 });
      const m = new THREE.Mesh(jarGeo, mat);
      m.position.set(-1.5 + i * 1, -0.3, -1.8);
      scene.add(m);
    });

    // Floating spheres
    [
      { pos: [-2.5, 1.5, -1.5], color: 0xff8c00 },
      { pos: [2, 1.8, -1.2], color: 0x32cd32 },
    ].forEach(({ pos, color }) => {
      const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.4 });
      const m = new THREE.Mesh(new THREE.SphereGeometry(0.1, 10, 10), mat);
      m.position.set(...pos);
      scene.add(m);
      objects.push({ obj: m, type: "float", offset: Math.random() * 6, baseY: pos[1] });
    });

    const clock = new THREE.Clock();
    return {
      update: () => {
        const t = clock.getElapsedTime();
        objects.forEach(o => {
          if (o.type === "pot") o.obj.position.y = o.baseY + Math.sin(t * 0.8 + o.offset) * 0.015;
          if (o.type === "steam") {
            const cycle = (t + o.offset) % 3;
            o.obj.position.y = o.baseY + cycle * 0.3;
            o.obj.material.opacity = Math.max(0, 0.35 - cycle * 0.12);
            o.obj.scale.setScalar(0.5 + cycle * 0.4);
          }
          if (o.type === "star") {
            o.obj.rotation.y += 0.02;
            const s = 1 + Math.sin(t * 2) * 0.15;
            o.obj.scale.setScalar(s);
          }
          if (o.type === "float") {
            o.obj.position.y = o.baseY + Math.sin(t + o.offset) * 0.2;
            o.obj.rotation.x += 0.005;
          }
        });
      },
    };
  });

  return <canvas ref={ref} style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', width: '100%', height: '100%' }} />;
};
